/**
 * Host Global - Property Rental Management System
 * Admin Dashboard
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import Icon from "@mui/material/Icon";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PropertyOwnerFooter from "examples/PropertyOwnerFooter";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Context
import { useAuth } from "context/authContext";

// API
import { propertyOwnerAPI } from "utils/api";

// Calendar components
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

const localizer = momentLocalizer(moment);

function Dashboard() {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    properties: 0,
    bookings: 0,
    revenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(true);
  const [showNotification, setShowNotification] = useState(true);
  const [profile, setProfile] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [calendarView, setCalendarView] = useState("month");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch property owner data
  useEffect(() => {
    const fetchPropertyOwnerData = async () => {
      if (userRole === "Property_Owner" || userRole === "Property Owner") {
        try {
          setLoading(true);
          const [propertiesResponse, profileResponse] = await Promise.all([
            propertyOwnerAPI.getMyProperties(),
            propertyOwnerAPI.getProfile().catch(() => ({ data: { wallet: 0 } })),
          ]);

          const properties = propertiesResponse.data || [];
          const profileData = profileResponse.data || {};
          setProfile(profileData);

          // Check if profile is complete (has idCard, address, nationality, and bank details)
          const hasIdCard = profileData.idCard && profileData.idCard.url;
          const hasAddress = profileData.address && profileData.address.trim() !== "";
          const hasNationality = profileData.nationality && profileData.nationality.trim() !== "";
          const hasBankDetails =
            profileData.bankDetails &&
            profileData.bankDetails.bankName &&
            profileData.bankDetails.accountNumber &&
            profileData.bankDetails.accountHolderName;
          const isComplete = hasIdCard && hasAddress && hasNationality && hasBankDetails;
          setProfileComplete(isComplete);

          // Calculate stats from properties
          const totalProperties = properties.length;
          const approvedProperties = properties.filter((p) => p.status === "Approved").length;

          // Fetch reservations for bookings count and calendar
          const reservationsResponse = await propertyOwnerAPI
            .getReservations()
            .catch(() => ({ data: [] }));
          const reservationsData = reservationsResponse.data || [];
          setReservations(reservationsData);
          const totalBookings = reservationsData.length;

          // Fetch wallet for revenue
          const walletResponse = await propertyOwnerAPI
            .getWallet()
            .catch(() => ({ wallet: { available_balance: 0 } }));
          const walletData = walletResponse.wallet || walletResponse.data || {};
          const totalRevenue = walletData.available_balance || 0;

          setStats({
            properties: totalProperties,
            approvedProperties,
            bookings: totalBookings,
            revenue: totalRevenue,
          });
        } catch (error) {
          console.error("Error fetching property owner data:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchPropertyOwnerData();
  }, [userRole]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Profile Completion Notification for Property Owners */}
        {(userRole === "Property_Owner" || userRole === "Property Owner") && !profileComplete && (
          <Collapse in={showNotification}>
            <MDBox mb={3}>
              <Card
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "12px",
                  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <MDBox
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "200px",
                    height: "200px",
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "50%",
                    transform: "translate(50%, -50%)",
                  }}
                />
                <MDBox p={3} position="relative">
                  <MDBox display="flex" alignItems="flex-start" justifyContent="space-between">
                    <MDBox display="flex" alignItems="flex-start" flex={1}>
                      <MDBox
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.2)",
                          borderRadius: "50%",
                          p: 1.5,
                          mr: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon sx={{ color: "white", fontSize: "2rem" }}>account_circle</Icon>
                      </MDBox>
                      <MDBox flex={1}>
                        <MDTypography variant="h6" fontWeight="bold" color="white" mb={1}>
                          Complete Your Profile
                        </MDTypography>
                        <MDTypography variant="body2" color="white" sx={{ opacity: 0.9, mb: 2 }}>
                          To begin listing your properties with us, please complete your profile by
                          adding your ID card, address, nationality, and bank details.
                        </MDTypography>
                        <MDBox display="flex" gap={1} flexWrap="wrap">
                          <MDButton
                            variant="contained"
                            color="white"
                            size="medium"
                            onClick={() => navigate("/profile")}
                            sx={{
                              backgroundColor: "white",
                              color: "#667eea",
                              fontWeight: "bold",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                              },
                            }}
                          >
                            <Icon sx={{ mr: 1 }}>edit</Icon>
                            Complete Profile
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                    <IconButton
                      onClick={() => setShowNotification(false)}
                      sx={{
                        color: "white",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                      }}
                    >
                      <Icon>close</Icon>
                    </IconButton>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </Collapse>
        )}

        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="medium">
            Welcome back, {user?.fullName || "User"}!
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {(userRole === "Property_Owner" || userRole === "Property Owner") &&
              "Manage your properties, view bookings, and track your earnings"}
            {userRole === "Admin" && "Manage properties, approve listings, and assign tasks"}
            {userRole === "Operations" &&
              "Handle property inspections and check-in/check-out operations"}
            {userRole === "Financial Team" && "View finances and manage payouts"}
            {userRole === "Booking Agent" && "Manage property bookings and reservations"}
            {userRole === "Check-In/Check-Out Agent" && "Handle guest check-ins and check-outs"}
            {userRole === "Housekeeping" && "Manage cleaning tasks and room status"}
            {userRole === "Marketing Team" && "Create campaigns and attract property owners"}
            {!userRole && "Property Rental Management Dashboard"}
          </MDTypography>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox
              mb={1.5}
              onClick={() => navigate("/property-owner/properties")}
              sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
            >
              <ComplexStatisticsCard
                color="dark"
                icon="home"
                title="My Properties"
                count={loading ? "..." : stats.properties}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Total listings",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox
              mb={1.5}
              onClick={() => navigate("/property-owner/reservations")}
              sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
            >
              <ComplexStatisticsCard
                icon="calendar_today"
                title="Bookings"
                count={loading ? "..." : stats.bookings}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Active reservations",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox
              mb={1.5}
              onClick={() => navigate("/property-owner/wallet")}
              sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
            >
              <ComplexStatisticsCard
                color="success"
                icon="account_balance"
                title="Revenue"
                count={
                  loading
                    ? "..."
                    : new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "EGP",
                        minimumFractionDigits: 0,
                      }).format(stats.revenue || 0)
                }
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Available balance",
                }}
              />
            </MDBox>
          </Grid>
          {userRole !== "Property_Owner" && userRole !== "Property Owner" && (
            <Grid item xs={12} md={6} lg={3}>
              <MDBox
                mb={1.5}
                onClick={() => {
                  if (userRole === "Super_Admin" || userRole === "Super Admin") {
                    navigate("/super-admin/property-owners");
                  } else {
                    navigate("/admin/property-owners");
                  }
                }}
                sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
              >
                <ComplexStatisticsCard
                  color="primary"
                  icon="people"
                  title="Property Owners"
                  count={0}
                  percentage={{
                    color: "success",
                    amount: "",
                    label: "Registered owners",
                  }}
                />
              </MDBox>
            </Grid>
          )}
          {(userRole === "Property_Owner" || userRole === "Property Owner") && (
            <Grid item xs={12} md={6} lg={3}>
              <MDBox
                mb={1.5}
                onClick={() => navigate("/property-owner/properties")}
                sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
              >
                <ComplexStatisticsCard
                  color="info"
                  icon="check_circle"
                  title="Approved"
                  count={loading ? "..." : stats.approvedProperties || 0}
                  percentage={{
                    color: "success",
                    amount: "",
                    label: "Approved properties",
                  }}
                />
              </MDBox>
            </Grid>
          )}
        </Grid>

        {(userRole === "Property_Owner" || userRole === "Property Owner") && (
          <MDBox mt={4.5}>
            <Grid container spacing={3}>
              {/* Calendar Controls */}
              <Grid item xs={12}>
                <Card sx={{ p: 3, mb: 3 }}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                  >
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() =>
                          setCalendarDate(
                            moment(calendarDate)
                              .subtract(
                                1,
                                calendarView === "month"
                                  ? "month"
                                  : calendarView === "week"
                                  ? "week"
                                  : "day"
                              )
                              .toDate()
                          )
                        }
                        startIcon={<Icon>chevron_left</Icon>}
                      >
                        Previous
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => setCalendarDate(new Date())}
                      >
                        Today
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() =>
                          setCalendarDate(
                            moment(calendarDate)
                              .add(
                                1,
                                calendarView === "month"
                                  ? "month"
                                  : calendarView === "week"
                                  ? "week"
                                  : "day"
                              )
                              .toDate()
                          )
                        }
                        endIcon={<Icon>chevron_right</Icon>}
                      >
                        Next
                      </MDButton>
                    </MDBox>
                    <ButtonGroup variant="outlined" size="small">
                      <Button
                        variant={calendarView === "month" ? "contained" : "outlined"}
                        onClick={() => setCalendarView("month")}
                      >
                        Month
                      </Button>
                      <Button
                        variant={calendarView === "week" ? "contained" : "outlined"}
                        onClick={() => setCalendarView("week")}
                      >
                        Week
                      </Button>
                      <Button
                        variant={calendarView === "day" ? "contained" : "outlined"}
                        onClick={() => setCalendarView("day")}
                      >
                        Day
                      </Button>
                    </ButtonGroup>
                  </MDBox>
                </Card>
              </Grid>

              {/* Calendar */}
              <Grid item xs={12}>
                <Card sx={{ p: 3 }}>
                  {reservations.length === 0 ? (
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={8}
                      sx={{ minHeight: "500px" }}
                    >
                      <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>
                        event_busy
                      </Icon>
                      <MDTypography
                        variant="h6"
                        style={{ textAlign: "center" }}
                        color="text.secondary"
                        mb={1}
                      >
                        No Reservations Found
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary">
                        You don't have any bookings yet. Reservations will appear here once clients
                        book your properties.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDBox
                      sx={{
                        height:
                          calendarView === "month"
                            ? "600px"
                            : calendarView === "week"
                            ? "500px"
                            : "400px",
                        "& .rbc-calendar": {
                          fontFamily: "inherit",
                        },
                        "& .rbc-header": {
                          padding: "12px 8px",
                          fontWeight: 600,
                          borderBottom: "2px solid #e0e0e0",
                        },
                        "& .rbc-today": {
                          backgroundColor: "rgba(25, 118, 210, 0.1)",
                        },
                        "& .rbc-off-range-bg": {
                          backgroundColor: "#f5f5f5",
                        },
                        "& .rbc-event": {
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.875rem",
                          cursor: "pointer",
                        },
                        "& .rbc-event:hover": {
                          opacity: 0.8,
                        },
                      }}
                    >
                      <Calendar
                        localizer={localizer}
                        events={reservations.map((reservation) => {
                          let color = "#1976d2";
                          if (reservation.status === "checked_in") color = "#4caf50";
                          else if (reservation.status === "checked_out") color = "#9e9e9e";
                          else if (reservation.status === "cancelled") color = "#f44336";

                          return {
                            id: reservation.reservation_id,
                            title: `${reservation.property_name} - ${reservation.reservation_number}`,
                            start: new Date(reservation.start_date),
                            end: new Date(reservation.end_date),
                            resource: reservation,
                            color: color,
                          };
                        })}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: "100%" }}
                        view={calendarView}
                        date={calendarDate}
                        onView={setCalendarView}
                        onNavigate={setCalendarDate}
                        onSelectEvent={(event) => {
                          setSelectedEvent(event.resource);
                          setDialogOpen(true);
                        }}
                        eventPropGetter={(event) => ({
                          style: {
                            backgroundColor: event.color || "#1976d2",
                            borderRadius: "4px",
                            opacity: 0.9,
                            color: "white",
                            border: "none",
                            display: "block",
                            padding: "4px 8px",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                          },
                        })}
                        popup
                        showMultiDayTimes
                      />
                    </MDBox>
                  )}
                </Card>
              </Grid>

              {/* Status Legend */}
              {reservations.length > 0 && (
                <Grid item xs={12}>
                  <Card sx={{ p: 2 }}>
                    <MDBox display="flex" gap={3} alignItems="center" flexWrap="wrap">
                      <MDTypography variant="button" fontWeight="medium">
                        Status Legend:
                      </MDTypography>
                      <Chip
                        label="Booked"
                        sx={{ bgcolor: "#1976d2", color: "white", fontWeight: 600 }}
                        size="small"
                      />
                      <Chip
                        label="Checked In"
                        sx={{ bgcolor: "#4caf50", color: "white", fontWeight: 600 }}
                        size="small"
                      />
                      <Chip
                        label="Checked Out"
                        sx={{ bgcolor: "#9e9e9e", color: "white", fontWeight: 600 }}
                        size="small"
                      />
                      <Chip
                        label="Cancelled"
                        sx={{ bgcolor: "#f44336", color: "white", fontWeight: 600 }}
                        size="small"
                      />
                    </MDBox>
                  </Card>
                </Grid>
              )}
            </Grid>
          </MDBox>
        )}

        {/* Reservation Details Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography
                variant="h5"
                fontWeight="bold"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Icon color="info">event</Icon>
                Reservation Details
              </MDTypography>
              <MDButton
                iconOnly
                variant="text"
                color="inherit"
                onClick={() => setDialogOpen(false)}
              >
                <Icon>close</Icon>
              </MDButton>
            </MDBox>
          </DialogTitle>
          <DialogContent dividers>
            {selectedEvent && (
              <MDBox>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Property Name
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold">
                    {selectedEvent.property_name}
                  </MDTypography>
                  {selectedEvent.property_short_id && (
                    <MDTypography variant="caption" color="text.secondary">
                      ID: {selectedEvent.property_short_id}
                    </MDTypography>
                  )}
                </MDBox>

                <Divider sx={{ my: 2 }} />

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Reservation Number
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="medium">
                    {selectedEvent.reservation_number}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Booking Dates
                  </MDTypography>
                  <MDTypography variant="body1">
                    {moment(selectedEvent.start_date).format("MMM DD, YYYY")} -{" "}
                    {moment(selectedEvent.end_date).format("MMM DD, YYYY")}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Status
                  </MDTypography>
                  <Chip
                    label={selectedEvent.status.replace("_", " ").toUpperCase()}
                    sx={{
                      bgcolor:
                        selectedEvent.status === "booked"
                          ? "#1976d2"
                          : selectedEvent.status === "checked_in"
                          ? "#4caf50"
                          : selectedEvent.status === "checked_out"
                          ? "#9e9e9e"
                          : "#f44336",
                      color: "white",
                      fontWeight: 600,
                    }}
                    size="small"
                  />
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Total Revenue
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold" color="success.main">
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "EGP",
                      minimumFractionDigits: 0,
                    }).format(selectedEvent.total_amount)}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Number of Guests
                  </MDTypography>
                  <MDTypography variant="body1">{selectedEvent.number_of_guests}</MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Client Name
                  </MDTypography>
                  <MDTypography variant="body1">{selectedEvent.client_name}</MDTypography>
                </MDBox>

                <MDBox>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Created At
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    {moment(selectedEvent.created_at).format("MMM DD, YYYY")}
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
            {selectedEvent?.reservation_id && (
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  setDialogOpen(false);
                  navigate(`/property-owner/reservations/${selectedEvent.reservation_id}`);
                }}
                sx={{ borderRadius: 2, mr: 1 }}
              >
                View Full Details
              </MDButton>
            )}
            <MDButton onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2 }}>
              Close
            </MDButton>
          </DialogActions>
        </Dialog>

        {(userRole === "Property_Owner" || userRole === "Property Owner") && (
          <MDBox mt={4.5}>
            <MDBox p={3} borderRadius="lg" bgColor="white" shadow="sm">
              <MDTypography variant="h5" fontWeight="medium" mb={2}>
                Quick Actions
              </MDTypography>
              <MDTypography variant="body2" color="text" mb={2}>
                Manage your property portfolio and track your rental business performance.
              </MDTypography>
              <MDTypography variant="body2" color="text">
                • View and manage your properties
                <br />• Track bookings and reservations
                <br />• Monitor revenue and earnings
                <br />• Update property information
              </MDTypography>
            </MDBox>
          </MDBox>
        )}
        {userRole !== "Property_Owner" && userRole !== "Property Owner" && (
          <MDBox mt={4.5}>
            <MDBox p={3} borderRadius="lg" bgColor="white" shadow="sm">
              <MDTypography variant="h5" fontWeight="medium" mb={2}>
                Quick Actions
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Dashboard content will be customized based on your role and permissions. Use the
                navigation menu to access specific features.
              </MDTypography>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      {userRole === "Property_Owner" || userRole === "Property Owner" ? (
        <PropertyOwnerFooter />
      ) : (
        <Footer />
      )}
    </DashboardLayout>
  );
}

export default Dashboard;
